// src/lib/userUtils.js
// All client-side utilities now avoid direct Supabase usage.
// For DB actions, go through apiClient.js to hit your BFF endpoints.

import { apiGet, bootstrapBffSession, setPseudoUidHint } from './apiClient';

/** SHA-256 -> hex using WebCrypto */
async function sha256Hex(input) {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Build a stable, pseudonymous ID from non-sensitive browser fields.
 * A soft anchor (e.g., IP or other entropy) keeps identifiers stable without
 * relying on sensitive values. Nothing here should uniquely fingerprint users.
 */
async function buildPseudonymousId(anchor = 'no-anchor') {
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const languages = Array.isArray(navigator.languages) ? navigator.languages.join(',') : (navigator.language || '');
  const vendor = navigator.vendor || '';
  const hardwareConcurrency = String(navigator.hardwareConcurrency || 0);
  const deviceMemory = String(navigator.deviceMemory || 0);
  const colorDepth = String((window.screen && window.screen.colorDepth) || '');
  const width = String((window.screen && window.screen.width) || '');
  const height = String((window.screen && window.screen.height) || '');
  const pixelRatio = String(window.devicePixelRatio || 1);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const doNotTrack = String(navigator.doNotTrack || window.doNotTrack || '');
  const maxTouch = String(navigator.maxTouchPoints || 0);

  const raw = JSON.stringify({
    anchor,
    ua, platform, languages, vendor,
    hardwareConcurrency, deviceMemory,
    colorDepth, width, height, pixelRatio,
    tz, doNotTrack, maxTouch,
  });

  const hex = await sha256Hex(raw);
  return 'uid_' + hex.slice(0, 16); // short, readable, still strong
}

async function fetchServerIdentity() {
  try {
    await bootstrapBffSession().catch(() => {});
    const data = await apiGet('/auth/identity');
    if (!data) return null;

    const actorId = data.actorId || null;
    return {
      user_id: actorId,
      ip: data.ip || 'unknown',
      city: data.city || null,
      region: data.region || null,
      country: data.countryCode || null,
      continent: data.continent || null,
      timezone: data.timezone || null,
      org: data.org || null,
      latitude: data.latitude ?? null,
      longitude: data.longitude ?? null,
      source: 'server',
    };
  } catch (err) {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
      console.warn('Failed to fetch identity from server:', err);
    }
    return null;
  }
}

/**
 * Get a stable, pseudonymous identity for the current device/browser.
 * Returns:
 * {
 *   user_id: 'uid_xxx',             // pseudonymous id
 *   ip, city, region, country, ...  // IP/geodata
 * }
 *
 * Cache:
 * - Identity object cached 24h (localStorage key: cached_identity)
 */
export async function getUserIdentity() {
  const now = Date.now();
  try {
    localStorage.removeItem('cached_ip_data');
  } catch {}

  // 1) Try cached identity (24h)
  const cached = localStorage.getItem('cached_identity');
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (now - (parsed._cachedAt || 0) < 24 * 60 * 60 * 1000) return parsed;
    } catch {}
  }

  // 2) Try to fetch server-issued identity (preferred)
  const serverIdentity = await fetchServerIdentity();
  let identity = serverIdentity;

  // 3) Fallback to client-derived pseudonymous ID if server had none
  if (!identity || !identity.user_id) {
    const anchor = serverIdentity?.ip && serverIdentity.ip !== 'unknown'
      ? serverIdentity.ip
      : 'client-only';
    const userId = await buildPseudonymousId(anchor);
    identity = {
      user_id: userId,
      ip: serverIdentity?.ip || 'unknown',
      city: serverIdentity?.city || null,
      region: serverIdentity?.region || null,
      country: serverIdentity?.country || null,
      continent: serverIdentity?.continent || null,
      timezone: serverIdentity?.timezone || null,
      org: serverIdentity?.org || null,
      latitude: serverIdentity?.latitude ?? null,
      longitude: serverIdentity?.longitude ?? null,
      source: serverIdentity?.source || 'client',
    };
  }

  identity._cachedAt = now;
  localStorage.setItem('cached_identity', JSON.stringify(identity));
  if (identity.user_id) {
    setPseudoUidHint(identity.user_id);
  }
  return identity;
}

/**
 * Backwards-compatible wrapper.
 * - withGeo === false -> returns STRING (user_id)
 * - withGeo === true  -> returns OBJECT (identity + ip/geodata)
 */
export async function getUserPublicIpKhoa(withGeo = false) {
  const ident = await getUserIdentity();
  return withGeo ? ident : ident.user_id;
}
